"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "./utils";

const RadixTooltip = TooltipPrimitive as any;
const hasRadixTooltip =
  typeof (RadixTooltip as any)?.Provider !== "undefined" &&
  typeof (RadixTooltip as any)?.Root !== "undefined" &&
  typeof (RadixTooltip as any)?.Trigger !== "undefined" &&
  typeof (RadixTooltip as any)?.Content !== "undefined";

let hasWarnedMissingRadixTooltip = false;
const warnMissingRadixTooltip = () => {
  if (hasWarnedMissingRadixTooltip) return;
  hasWarnedMissingRadixTooltip = true;
  console.warn(
    "[ui/tooltip] Radix Tooltip primitives unavailable. Falling back to basic markup.",
  );
};

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  if (!hasRadixTooltip || typeof (RadixTooltip as any)?.Provider === "undefined") {
    warnMissingRadixTooltip();
    const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
    return (
      <div data-slot="tooltip-provider-fallback" {...cleanProps} />
    );
  }
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <RadixTooltip.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...cleanProps}
    />
  );
}

/** The trigger element, so the root can tell when the same element also opens
 *  a menu or popover (it then carries aria-expanded="true"). */
const TooltipTriggerRefContext = React.createContext<React.MutableRefObject<HTMLElement | null> | null>(null);

const triggerExpanded = (el: HTMLElement | null) => el?.getAttribute("aria-expanded") === "true";

function Tooltip({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const open = openProp ?? uncontrolledOpen;
  // Set when the trigger's menu closes and hands focus back to it, which
  // would otherwise pop the tooltip straight up; cleared once the pointer or
  // focus leaves the trigger.
  const suppressed = React.useRef(false);
  const setOpen = React.useCallback((next: boolean) => {
    // A tooltip never sits over the menu its own trigger opened.
    if (next && (suppressed.current || triggerExpanded(triggerRef.current))) return;
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange, openProp]);

  // Only triggers that open a menu or popover carry aria-expanded, so a plain
  // tooltip never watches anything.
  React.useEffect(() => {
    const el = triggerRef.current;
    if (!el || !el.hasAttribute("aria-expanded") || typeof MutationObserver === "undefined") return;
    let expanded = triggerExpanded(el);
    const release = () => { suppressed.current = false; };
    const observer = new MutationObserver(() => {
      const now = triggerExpanded(el);
      if (now) setOpen(false);
      else if (expanded) suppressed.current = true;
      expanded = now;
    });
    observer.observe(el, { attributes: true, attributeFilter: ["aria-expanded"] });
    el.addEventListener("pointerleave", release);
    el.addEventListener("blur", release);
    return () => {
      observer.disconnect();
      el.removeEventListener("pointerleave", release);
      el.removeEventListener("blur", release);
    };
  }, [setOpen]);

  if (!hasRadixTooltip || typeof (RadixTooltip as any)?.Root === "undefined") {
    warnMissingRadixTooltip();
    const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
    return <div data-slot="tooltip-fallback" {...cleanProps} />;
  }
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <TooltipTriggerRefContext.Provider value={triggerRef}>
      <TooltipProvider>
        <RadixTooltip.Root data-slot="tooltip" {...cleanProps} open={open} onOpenChange={setOpen} />
      </TooltipProvider>
    </TooltipTriggerRefContext.Provider>
  );
}

function TooltipTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger> & { asChild?: boolean }) {
  const triggerRef = React.useContext(TooltipTriggerRefContext);
  const forwardedRef = (props as { ref?: React.Ref<HTMLElement> }).ref;
  // Stable across renders: a new ref callback every render makes Radix's
  // composed trigger ref set state on each commit, which loops under React 19.
  const setTriggerRef = React.useCallback((el: HTMLElement | null) => {
    if (triggerRef) triggerRef.current = el;
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLElement | null>).current = el;
  }, [triggerRef, forwardedRef]);
  if (!hasRadixTooltip || typeof (RadixTooltip as any)?.Trigger === "undefined") {
    warnMissingRadixTooltip();
    const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
    return <span data-slot="tooltip-trigger-fallback" {...cleanProps}>{children}</span>;
  }
  const shouldUseAsChild =
    typeof asChild === "boolean" ? asChild : React.isValidElement(children);
  
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  
  let cleanChildren = children;
  if (shouldUseAsChild && React.isValidElement(children)) {
    const childProps = children.props as any;
    const { _fgT: _1, _fgt: _2, _fgS: _3, _fgs: _4, _fgB: _5, _fgb: _6, ...cleanChildProps } = childProps;
    cleanChildren = React.cloneElement(children, cleanChildProps);
  }
  
  return (
    <RadixTooltip.Trigger
      data-slot="tooltip-trigger"
      asChild={shouldUseAsChild}
      {...cleanProps}
      ref={setTriggerRef}
    >
      {cleanChildren}
    </RadixTooltip.Trigger>
  );
}

function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  if (!hasRadixTooltip || typeof (RadixTooltip as any)?.Content === "undefined") {
    warnMissingRadixTooltip();
    return null;
  }
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-tooltip text-tooltip-foreground shadow-(--tooltip-shadow) animate-in fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 z-50 inline-flex w-fit max-w-xs items-center gap-2 origin-(--radix-tooltip-content-transform-origin) rounded-2xl px-3 py-[5px] text-sm font-semibold text-balance",
          className,
        )}
        {...cleanProps}
      >
        {children}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
}

/** A keyboard shortcut shown inside a tooltip, after its label. */
function TooltipShortcut({ className, ...props }: React.ComponentProps<"kbd">) {
  return (
    <kbd
      data-slot="tooltip-shortcut"
      className={cn("rounded-lg bg-white/25 px-1.5 font-sans text-sm leading-[18px] font-semibold", className)}
      {...props}
    />
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipShortcut };
